import { useTranslation } from "react-i18next";
import { useRequest } from "hooks/useRequest";
import api from "utils/api";
import { CABLE_TAKEOFF_BETA_ORGANIZATION_IDS } from "../../constants";
import { canViewSmartSurveyorReview } from "../../utils/canViewSmartSurveyorReview";
import { fetchBOQExtraction } from "../../utils/fetchBOQExtraction";
import { fetchResearchReport } from "../../utils/fetchResearchReport";
import { fetchCables, fetchCablesPublished } from "../../utils/fetchCables";
import type {
  UseProjectMetadataArgs,
  ProjectMetadata,
  UseProjectMetadataResult,
} from "./types";

export const useProjectMetadata = ({
  projectId,
  isSuperAdmin,
  onUploadNotReady,
}: UseProjectMetadataArgs): UseProjectMetadataResult => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const {
    isLoading,
    data: metadata,
    error,
  } = useRequest<ProjectMetadata | undefined, []>(
    async () => {
      if (!projectId) return;

      const { project } = await api.fetchProject(projectId);

      if (
        !canViewSmartSurveyorReview({ status: project?.status, isSuperAdmin })
      ) {
        onUploadNotReady();
        return;
      }

      const canAccessCableTakeoffBeta =
        isSuperAdmin ||
        CABLE_TAKEOFF_BETA_ORGANIZATION_IDS.includes(
          Number(project?.organizationId),
        );

      const [boqExtraction, researchReport, cablesPublished] =
        await Promise.all([
          fetchBOQExtraction(projectId),
          fetchResearchReport(projectId),
          // Cables (beta) — skip the fetch for users without beta access.
          canAccessCableTakeoffBeta
            ? fetchCablesPublished(projectId)
            : Promise.resolve(null),
        ]);

      // Beta gate: only load + show cables when published & enabled. Version the
      // cables fetch by published_at so re-publishing an estimation busts the cache.
      const cablesEnabled = !!cablesPublished?.enabled;
      const cables = cablesEnabled ? await fetchCables(projectId, lang) : null;

      return {
        project,
        boqExtraction,
        hasResearchReport: !!researchReport,
        cables,
        cablesPublished,
        canAccessCableTakeoffBeta,
      };
    },
    { manual: false, defaultParams: [] },
  );

  return {
    isLoading,
    metadata,
    error,
  };
};
